import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { HighlightColor, Annotation, HighlightPosition } from './types';
import { v4 as uuidv4 } from 'uuid';
import { useSessionContext } from '../../components/SessionContext';
import { useDocumentContext } from '../../components/DocumentContext';
import { getHighlightPath } from '../Highlighting/utils/highlightPositionUtils';
import { ToolbarPosition } from '../Highlighting/types/toolbar';

type ToolbarData = {
  color: HighlightColor | null;
  text: string | null;
  position: HighlightPosition | null;
};

type AnnotationToolbarContextType = {
  selectedAnnotation: Annotation | null;
  isToolbarOpen: boolean;
  toolbarData: ToolbarData;
  toolbarPosition: ToolbarPosition | null;
  openToolbarForSelection: (
    position: ToolbarPosition,
    text: string,
    highlightPosition: HighlightPosition,
  ) => void;
  openToolbarForAnnotation: (
    position: ToolbarPosition,
    annotation: Annotation,
  ) => void;
  closeToolbar: () => void;
  updateToolbarData: (
    updatedToolbarData: Partial<ToolbarData>,
  ) => Promise<Annotation>;
  handleAnnotationDelete: (annotation: Annotation) => void;
};

export const AnnotationToolbarContext =
  createContext<AnnotationToolbarContextType | null>(null);

export function AnnotationToolbarProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const [isToolbarOpen, setIsToolbarOpen] = useState(false);
  const [toolbarPosition, setToolbarPosition] =
    useState<ToolbarPosition | null>(null);
  const [selectedAnnotation, setSelectedAnnotation] =
    useState<Annotation | null>(null);
  const [toolbarData, setToolbarData] = useState<ToolbarData>({
    color: null,
    text: null,
    position: null,
  });

  const { addAnnotation, updateAnnotation, removeAnnotation } =
    useSessionContext();
  const { chapters } = useDocumentContext();

  // Empty toolbar data when toolbar is closed
  useEffect(() => {
    if (!isToolbarOpen) {
      setSelectedAnnotation(null);
      setToolbarData({
        color: null,
        text: null,
        position: null,
      });
    }
  }, [isToolbarOpen]);

  // --- Toolbar handlers ---

  const openToolbarForSelection = useCallback(
    (
      position: ToolbarPosition,
      text: string,
      highlightPosition: HighlightPosition,
    ) => {
      setToolbarData({
        color: null,
        position: highlightPosition,
        text,
      });
      setToolbarPosition(position);
      setIsToolbarOpen(true);
    },
    [],
  );
  const openToolbarForAnnotation = useCallback(
    (position: ToolbarPosition, annotation: Annotation) => {
      setSelectedAnnotation(annotation);
      setToolbarData({
        color: annotation.display.color,
        position: annotation.position,
        text: annotation.text.content,
      });
      setToolbarPosition(position);
      setIsToolbarOpen(true);
    },
    [],
  );

  const closeToolbar = useCallback(() => {
    setIsToolbarOpen(false);
    setToolbarPosition(null);
    setSelectedAnnotation(null);
  }, []);

  // --- Annotation handlers --- //

  const handleAnnotationDelete = useCallback(
    (annotation: Annotation) => {
      // TODO: Delete the annotation in the backend

      closeToolbar();
      removeAnnotation(annotation);
    },
    [closeToolbar, removeAnnotation],
  );

  const handleHighlightCreation = useCallback(
    async (newToolbarData: ToolbarData): Promise<Annotation> => {
      // TODO: Create an annotation in the backend

      // This is a temporary annotation creation for testing purposes
      const newAnnotation: Annotation = {
        id: uuidv4(),
        created: new Date().toISOString(),
        text: {
          prefix: '',
          content: newToolbarData.text ?? '',
          suffix: '',
        },
        path: getHighlightPath(
          newToolbarData.position as HighlightPosition,
          chapters ?? [],
        ),
        note: null,
        display: {
          color: newToolbarData.color,
        },
        position: newToolbarData.position as HighlightPosition, // We know the data is not null when creating an annotation
      };

      const createdAnnotation = addAnnotation(newAnnotation);

      setSelectedAnnotation(createdAnnotation);
      setToolbarData({
        color: newAnnotation.display.color,
        text: newAnnotation.text.content,
        position: newAnnotation.position,
      });

      return createdAnnotation;
    },
    [addAnnotation, chapters],
  );

  const updateToolbarData = useCallback(
    async (updatedToolbarData: Partial<ToolbarData>): Promise<Annotation> => {
      setToolbarData((prev) => ({ ...prev, ...updatedToolbarData }));

      if (selectedAnnotation) {
        const updatedAnnotationObject = selectedAnnotation;

        if (updatedToolbarData.color) {
          updatedAnnotationObject.display.color = updatedToolbarData.color;
        }

        // TODO: Update the annotation in the backend

        return updateAnnotation(updatedAnnotationObject);
      } else {
        return handleHighlightCreation({
          ...toolbarData,
          ...updatedToolbarData,
        });
      }
    },
    [
      selectedAnnotation,
      updateAnnotation,
      handleHighlightCreation,
      toolbarData,
    ],
  );

  const value = useMemo(
    () => ({
      selectedAnnotation,
      isToolbarOpen,
      openToolbarForSelection,
      openToolbarForAnnotation,
      toolbarPosition,
      toolbarData,
      closeToolbar,
      updateToolbarData,
      handleAnnotationDelete,
    }),
    [
      selectedAnnotation,
      isToolbarOpen,
      openToolbarForSelection,
      openToolbarForAnnotation,
      toolbarPosition,
      toolbarData,
      closeToolbar,
      updateToolbarData,
      handleAnnotationDelete,
    ],
  );

  return (
    <AnnotationToolbarContext.Provider value={value}>
      {children}
    </AnnotationToolbarContext.Provider>
  );
}

export function useAnnotationToolbarContext() {
  const context = useContext(AnnotationToolbarContext);

  if (!context) {
    throw new Error(
      'useAnnotationToolbarContext must be used within a AnnotationToolbarProvider',
    );
  }
  return context;
}
